import { useCallback, useEffect, useRef, useState } from 'react';

export type SaveStatus = 'idle' | 'saving' | 'saved' | 'unsaved';

const BACKOFF_MS = [2000, 4000, 8000, 15000, 30000];

/**
 * Latest-wins save queue with automatic retry.
 *
 * A typed value is NEVER discarded: if the save fails the payload is held in
 * `pending`, the status flips to 'unsaved', and a retry is scheduled with
 * exponential backoff (and immediately when the browser comes back online).
 * A newer save simply replaces the pending payload.
 */
export function useResilientSave<T>(fn: (payload: T) => Promise<void>) {
  const [status, setStatus] = useState<SaveStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const pendingRef = useRef<T | null>(null);
  const attemptRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const runningRef = useRef(false);
  const fnRef = useRef(fn);
  fnRef.current = fn;

  const clearTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const flush = useCallback(async () => {
    if (runningRef.current) return;
    const payload = pendingRef.current;
    if (payload == null) return;
    runningRef.current = true;
    setStatus('saving');
    try {
      await fnRef.current(payload);
      // Only clear if nothing newer arrived while we were in flight.
      if (pendingRef.current === payload) {
        pendingRef.current = null;
        attemptRef.current = 0;
        setError(null);
        setStatus('saved');
      }
    } catch (e: any) {
      attemptRef.current += 1;
      setError(e?.message || 'Could not save — will keep trying.');
      setStatus('unsaved');
      const delay = BACKOFF_MS[Math.min(attemptRef.current - 1, BACKOFF_MS.length - 1)];
      clearTimer();
      timerRef.current = setTimeout(() => {
        void flush();
      }, delay);
    } finally {
      runningRef.current = false;
      if (pendingRef.current != null && attemptRef.current === 0) {
        void flush();
      }
    }
  }, []);

  const save = useCallback(
    (payload: T) => {
      pendingRef.current = payload;
      clearTimer();
      void flush();
    },
    [flush],
  );

  const retry = useCallback(() => {
    attemptRef.current = 0;
    clearTimer();
    void flush();
  }, [flush]);

  useEffect(() => {
    const onOnline = () => {
      if (pendingRef.current != null) retry();
    };
    window.addEventListener('online', onOnline);
    return () => {
      window.removeEventListener('online', onOnline);
      clearTimer();
    };
  }, [retry]);

  return {
    status,
    error,
    save,
    retry,
    hasPending: pendingRef.current != null,
  };
}
